import React, {
  useState,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { DialogActions } from '@mui/material';

import FirebaseContext from '../../../../../context/FirebaseContext';
import { sendContract } from '../../../../utils/message';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useToast from '../../../../hooks/useToast';
import DialogModal from '../../../../../components/DialogModal';
import { Title } from '../../../../../components/v2/Header';
import InputLabel from '../../../../../components/v2/InputLabel';
import LoadingOverlay from '../../../LoadingOverlay';
import RadioButtonGroup from '../../../RadioButtonGroup';
import { ContactOptions } from '../../../../Model/Lead';
import { getOnboardingLinkFromLead } from '../../../../utils/onboardingLink';

import {
  ModalContent,
  SelectContainer,
  StyledButton,
} from './styles';
import texts from './texts';

const SendOnboardingLinkModal = ({
  contractDoc,
  showModal,
  onClose,
  onLinkSent,
}) => {
  const {
    userEmail,
    phoneNumber,
    leadId,
    coachName,
  } = contractDoc;

  const [selectedOption, setSelectedOption] = useState(ContactOptions.EMAIL);
  const [isLoading, setIsLoading] = useState(false);

  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { firebase: { remote } } = useContext(FirebaseContext);

  const messageOptions = useMemo(() => Object.values(ContactOptions).map((option) => ({
    label: format(texts.contactOptions[option], { contact: ContactOptions.EMAIL === option ? userEmail : phoneNumber }),
    value: option,
  })), [userEmail, phoneNumber]);

  const sendContractLink = useCallback(async () => {
    try {
      setIsLoading(true);
      const onboardingUrl = getOnboardingLinkFromLead(leadId);

      const sendData = {
        msgProvider: selectedOption,
        leadId,
        coachName,
        onboardingUrl,
      };

      await sendContract(sendData, remote);
      if (isComponentMountedRef.current) {
        showToast(texts.success);
        onLinkSent();
      }
    } catch (error) {
      showToast(format(texts.fail, { error }), { error: true });
    } finally {
      onClose();
      setIsLoading(false);
    }
  }, [
    leadId,
    coachName,
    remote,
    showToast,
    selectedOption,
    onLinkSent,
    onClose,
    isComponentMountedRef,
  ]);

  return (
    <>
      <DialogModal
        open={showModal}
        onClose={onClose}
        maxWidth="lg"
      >
        <ModalContent>
          <Title>{texts.title}</Title>
          <SelectContainer>
            <InputLabel>{texts.selectOption}</InputLabel>
            <RadioButtonGroup
              options={messageOptions}
              showStyledOptions={false}
              selectedOption={selectedOption}
              onOptionChange={setSelectedOption}
              isRow={false}
            />
          </SelectContainer>
          <DialogActions>
            <StyledButton onClick={sendContractLink}>{texts.sendContract}</StyledButton>
          </DialogActions>
        </ModalContent>
      </DialogModal>
      <LoadingOverlay isLoading={isLoading} />
    </>
  );
};

SendOnboardingLinkModal.propTypes = {
  contractDoc: PropTypes.object.isRequired,
  showModal: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onLinkSent: PropTypes.func.isRequired,
};

export default SendOnboardingLinkModal;
